import { useEffect, useRef } from "react";
import { MapPreferences, MapStyleId } from "../types";
import { MapService } from "../services/mapService";
import debugx from "debugx";

const debug = debugx("bikedash:map-sync");

// Map style URLs helper
function getMapStyleUrl(
  styleId: string,
  apiKeys?: Record<string, string>,
): string | null {
  const [provider, style] = styleId.split(":");

  const urls: Record<string, string> = {
    "osm:standard": "https://{a-c}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    "osm:humanitarian":
      "https://{a-c}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png",
    "osm:topographic": "https://{a-c}.tile.opentopomap.org/{z}/{x}/{y}.png",
    "cartodb:light":
      "https://{a-d}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png",
    "cartodb:dark":
      "https://{a-d}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png",
    "cartodb:voyager":
      "https://{a-d}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png",
    "esri:gray":
      "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}",
    "esri:satellite":
      "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
  };

  // Thunderforest styles need API key
  if (provider === "thunderforest") {
    // First try from cached auth
    let apiKey = null;
    const cachedAuth = localStorage.getItem("bikedash_cached_auth");
    if (cachedAuth) {
      try {
        const cached = JSON.parse(cachedAuth);
        apiKey = cached.thunderforestApiKey;
      } catch (e) {
        debug.warn("Failed to get Thunderforest API key from cached auth");
      }
    }

    // Fallback to manual override from apiKeys
    if (!apiKey && apiKeys?.thunderforest) {
      apiKey = apiKeys.thunderforest;
    }

    if (apiKey) {
      const tfUrls: Record<string, string> = {
        "thunderforest:cycle": `https://{a-c}.tile.thunderforest.com/cycle/{z}/{x}/{y}.png?apikey=${apiKey}`,
        "thunderforest:landscape": `https://{a-c}.tile.thunderforest.com/landscape/{z}/{x}/{y}.png?apikey=${apiKey}`,
        "thunderforest:outdoors": `https://{a-c}.tile.thunderforest.com/outdoors/{z}/{x}/{y}.png?apikey=${apiKey}`,
        "thunderforest:transport": `https://{a-c}.tile.thunderforest.com/transport/{z}/{x}/{y}.png?apikey=${apiKey}`,
        "thunderforest:transport-dark": `https://{a-c}.tile.thunderforest.com/transport-dark/{z}/{x}/{y}.png?apikey=${apiKey}`,
        "thunderforest:spinal-map": `https://{a-c}.tile.thunderforest.com/spinal-map/{z}/{x}/{y}.png?apikey=${apiKey}`,
        "thunderforest:pioneer": `https://{a-c}.tile.thunderforest.com/pioneer/{z}/{x}/{y}.png?apikey=${apiKey}`,
        "thunderforest:mobile-atlas": `https://{a-c}.tile.thunderforest.com/mobile-atlas/{z}/{x}/{y}.png?apikey=${apiKey}`,
        "thunderforest:neighbourhood": `https://{a-c}.tile.thunderforest.com/neighbourhood/{z}/{x}/{y}.png?apikey=${apiKey}`,
      };
      return tfUrls[styleId] || null;
    }
  }

  return urls[styleId] || null;
}

/**
 * Hook to sync map preferences to the MapService
 * Uses non-destructive mutations to avoid map re-renders
 */
export function useMapSync(
  mapPreferences: MapPreferences,
  mapService: MapService | null,
  apiKeys?: Record<string, string>,
) {
  const prevPrefsRef = useRef<MapPreferences | null>(null);
  const transitionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const latestPrefsRef = useRef<MapPreferences>(mapPreferences);
  const loadDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const lastLoadedStyleRef = useRef<string | null>(null);
  const isLoadingRef = useRef(false);

  // Subscribe to loading state changes
  useEffect(() => {
    if (!mapService) return;

    const unsubscribe = mapService.onLoadingChange((loading: boolean) => {
      isLoadingRef.current = loading;

      // When loading completes, check if we need to load the correct map
      if (!loading) {
        const currentPrefs = latestPrefsRef.current;
        const requestedStyle = currentPrefs.defaultStyleId;
        const loadedStyle = lastLoadedStyleRef.current;

        // If what we loaded doesn't match what's currently selected, load again
        if (requestedStyle && requestedStyle !== loadedStyle) {
          debug(
            "Loading completed but style mismatch. Requested: %s, Loaded: %s",
            requestedStyle,
            loadedStyle,
          );

          // If there's already a pending debounce, let it handle the correction
          if (loadDebounceRef.current) {
            debug("Pending debounce will load correct style");
            return;
          }

          // Schedule a debounced load to allow for rapid changes
          loadDebounceRef.current = setTimeout(() => {
            const currentStyle = latestPrefsRef.current.defaultStyleId;
            const currentOpacity = latestPrefsRef.current.overlayOpacity || 0;
            const currentStyleUrl = getMapStyleUrl(currentStyle, apiKeys);

            if (
              currentStyleUrl &&
              currentStyle !== lastLoadedStyleRef.current
            ) {
              debug("Loading correction: %s", currentStyle);
              lastLoadedStyleRef.current = currentStyle;

              // Check if we're in satellite mode
              const inSatelliteMode = currentOpacity > 0 && currentOpacity < 1;
              if (inSatelliteMode) {
                // Load as overlay in satellite mode
                const mapOverlayOpacity = 1 - currentOpacity;
                mapService.setOverlayMapStyle(
                  currentStyle as MapStyleId,
                  currentStyleUrl,
                  mapOverlayOpacity,
                );
              } else {
                // Load as base map in normal mode
                mapService.setBaseMapStyle(
                  currentStyle as MapStyleId,
                  currentStyleUrl,
                );
              }
            }
            loadDebounceRef.current = null;
          }, 300);
        }
      }
    });

    return unsubscribe;
  }, [mapService, apiKeys]);

  useEffect(() => {
    // Always keep latest prefs in ref for timeout callbacks
    latestPrefsRef.current = mapPreferences;

    if (!mapService) return;

    const prevPrefs = prevPrefsRef.current;

    // Apply initial settings if this is the first run
    if (!prevPrefs) {
      if (mapPreferences.activityTrack) {
        mapService.updateTrackStyle(mapPreferences.activityTrack);
      }
      if (mapPreferences.routeTrack) {
        mapService.updateRouteStyle(mapPreferences.routeTrack);
      }
    }
    const needsFullStyleUpdate =
      !prevPrefs ||
      prevPrefs.defaultStyleId !== mapPreferences.defaultStyleId ||
      prevPrefs.satelliteUnderlay !== mapPreferences.satelliteUnderlay;

    // Handle map style changes
    if (needsFullStyleUpdate) {
      const { defaultStyleId, overlayOpacity } = mapPreferences;
      const prevOpacity = prevPrefs?.overlayOpacity || 0;
      const opacity = overlayOpacity || 0;

      // Check if we're transitioning between satellite and normal modes
      // 0% = map only (OFF), 1-80% = blended with satellite
      const wasInSatelliteMode = prevOpacity > 0 && prevOpacity < 1;
      const isInSatelliteMode = opacity > 0 && opacity < 1;

      if (wasInSatelliteMode && !isInSatelliteMode) {
        // Transitioning FROM satellite blend to map only
        // 0 means "OFF" - show map only
        if (opacity === 0 && defaultStyleId) {
          // Debounce this transition to allow rapid style/mode changes
          if (loadDebounceRef.current) {
            clearTimeout(loadDebounceRef.current);
          }

          loadDebounceRef.current = setTimeout(() => {
            const currentStyle = latestPrefsRef.current.defaultStyleId;
            const currentOpacity = latestPrefsRef.current.overlayOpacity || 0;
            const currentStyleUrl = getMapStyleUrl(currentStyle, apiKeys);

            // Check if we're still supposed to be in pure map mode
            if (currentStyleUrl && currentOpacity === 0) {
              debug(
                "Transitioning from satellite to pure map: %s",
                currentStyle,
              );
              lastLoadedStyleRef.current = currentStyle;
              mapService.transitionFromSatelliteUnderlay(
                currentStyle as MapStyleId,
                currentStyleUrl,
              );
            }
            loadDebounceRef.current = null;
          }, 300);
        }
      } else if (!wasInSatelliteMode && isInSatelliteMode) {
        // Transitioning TO satellite blend mode
        if (defaultStyleId) {
          // Debounce this transition to allow rapid style/mode changes
          if (loadDebounceRef.current) {
            clearTimeout(loadDebounceRef.current);
          }

          loadDebounceRef.current = setTimeout(() => {
            const currentStyle = latestPrefsRef.current.defaultStyleId;
            const currentOpacity = latestPrefsRef.current.overlayOpacity || 0;
            const currentStyleUrl = getMapStyleUrl(currentStyle, apiKeys);

            // Check if we're still supposed to be in satellite mode
            const stillInSatelliteMode =
              currentOpacity > 0 && currentOpacity < 1;
            if (currentStyleUrl && stillInSatelliteMode) {
              const mapOverlayOpacity = 1 - currentOpacity;
              debug("Transitioning to satellite underlay: %s", currentStyle);
              lastLoadedStyleRef.current = currentStyle;
              mapService.transitionToSatelliteUnderlay(
                currentStyle as MapStyleId,
                currentStyleUrl,
                mapOverlayOpacity,
              );
            }
            loadDebounceRef.current = null;
          }, 300);
        }
      } else if (isInSatelliteMode) {
        // Already in satellite blend mode, changing the overlay map style
        if (defaultStyleId) {
          const isInitialLoad = !prevPrefs;

          if (isInitialLoad) {
            // Load immediately on initial load
            const overlayUrl = getMapStyleUrl(defaultStyleId, apiKeys);
            if (overlayUrl) {
              const mapOverlayOpacity = 1 - opacity;
              debug("Initial satellite overlay load: %s", defaultStyleId);
              lastLoadedStyleRef.current = defaultStyleId;
              mapService.setOverlayMapStyle(
                defaultStyleId as MapStyleId,
                overlayUrl,
                mapOverlayOpacity,
              );
            }
          } else {
            // Debounce overlay style changes to allow rapid cycling
            if (loadDebounceRef.current) {
              clearTimeout(loadDebounceRef.current);
            }

            loadDebounceRef.current = setTimeout(() => {
              const currentStyle = latestPrefsRef.current.defaultStyleId;
              const currentOpacity = latestPrefsRef.current.overlayOpacity || 0;
              const currentStyleUrl = getMapStyleUrl(currentStyle, apiKeys);
              if (currentStyleUrl) {
                const mapOverlayOpacity = 1 - currentOpacity;
                debug("Loading debounced satellite overlay: %s", currentStyle);
                lastLoadedStyleRef.current = currentStyle;
                mapService.setOverlayMapStyle(
                  currentStyle as MapStyleId,
                  currentStyleUrl,
                  mapOverlayOpacity,
                );
              }
              loadDebounceRef.current = null;
            }, 300);
          }
        }
      } else if (opacity === 0) {
        // Pure map mode (0% means OFF)
        if (defaultStyleId) {
          const styleUrl = getMapStyleUrl(defaultStyleId, apiKeys);
          if (styleUrl) {
            // Initial load or style change?
            const isInitialLoad = !prevPrefs;

            if (isInitialLoad) {
              // Load immediately on initial load
              debug("Initial map load: %s", defaultStyleId);
              lastLoadedStyleRef.current = defaultStyleId;
              mapService.setBaseMapStyle(
                defaultStyleId as MapStyleId,
                styleUrl,
              );
            } else {
              // Debounce subsequent style changes to allow rapid cycling
              if (loadDebounceRef.current) {
                clearTimeout(loadDebounceRef.current);
              }

              loadDebounceRef.current = setTimeout(() => {
                const currentStyle = latestPrefsRef.current.defaultStyleId;
                const currentStyleUrl = getMapStyleUrl(currentStyle, apiKeys);
                if (currentStyleUrl) {
                  debug("Loading debounced map style: %s", currentStyle);
                  lastLoadedStyleRef.current = currentStyle;
                  mapService.setBaseMapStyle(
                    currentStyle as MapStyleId,
                    currentStyleUrl,
                  );
                }
                loadDebounceRef.current = null;
              }, 300); // 300ms debounce
            }
          }
        }
      }
    } else if (
      prevPrefs &&
      prevPrefs.overlayOpacity !== mapPreferences.overlayOpacity
    ) {
      const opacity = mapPreferences.overlayOpacity || 0;
      const prevOpacity = prevPrefs.overlayOpacity || 0;

      // Check if we're transitioning between OFF and blend states
      const wasOff = prevOpacity === 0;
      const isOff = opacity === 0;
      const wasBlended = prevOpacity > 0 && prevOpacity < 1;
      const isBlended = opacity > 0 && opacity < 1;

      if (wasOff && isBlended) {
        // Transitioning FROM OFF TO satellite blend
        // Cancel any pending transition
        if (transitionTimeoutRef.current) {
          clearTimeout(transitionTimeoutRef.current);
        }

        // Use a small timeout to ensure we get the final value when rapidly sliding
        transitionTimeoutRef.current = setTimeout(() => {
          // Use the latest prefs from the ref, not the closure
          const currentPrefs = latestPrefsRef.current;
          const currentOpacity = currentPrefs.overlayOpacity || 0;

          if (currentOpacity > 0 && currentPrefs.defaultStyleId) {
            const overlayUrl = getMapStyleUrl(
              currentPrefs.defaultStyleId,
              apiKeys,
            );
            if (overlayUrl) {
              // Use the current opacity from the ref, not from when transition started
              const mapOverlayOpacity = 1 - currentOpacity;
              mapService.transitionToSatelliteUnderlay(
                currentPrefs.defaultStyleId as MapStyleId,
                overlayUrl,
                mapOverlayOpacity,
              );
            }
          }
          transitionTimeoutRef.current = null;
        }, 100); // Small delay to catch rapid changes
      } else if (wasBlended && isOff) {
        // Transitioning FROM blend TO OFF (pure map)
        // Debounce this to avoid removing overlay during rapid slider changes
        if (transitionTimeoutRef.current) {
          clearTimeout(transitionTimeoutRef.current);
        }

        transitionTimeoutRef.current = setTimeout(() => {
          const currentPrefs = latestPrefsRef.current;
          const currentOpacity = currentPrefs.overlayOpacity || 0;

          // Only transition if we're still at 0%
          if (currentOpacity === 0 && currentPrefs.defaultStyleId) {
            const styleUrl = getMapStyleUrl(
              currentPrefs.defaultStyleId,
              apiKeys,
            );
            if (styleUrl) {
              debug("Transitioning from satellite blend to OFF");
              mapService.transitionFromSatelliteUnderlay(
                currentPrefs.defaultStyleId as MapStyleId,
                styleUrl,
              );
            }
          }
          transitionTimeoutRef.current = null;
        }, 200); // Debounce to catch rapid slider changes
      } else if (wasBlended && isBlended) {
        // Just changing opacity within blend range
        const mapOverlayOpacity = 1 - opacity;
        mapService.updateOverlayOpacity(mapOverlayOpacity);
      }
    }

    // Handle track style changes
    if (
      prevPrefs &&
      JSON.stringify(prevPrefs.activityTrack) !==
        JSON.stringify(mapPreferences.activityTrack)
    ) {
      if (mapPreferences.activityTrack) {
        mapService.updateTrackStyle(mapPreferences.activityTrack);
      }
    }

    // Handle route style changes
    if (
      prevPrefs &&
      JSON.stringify(prevPrefs.routeTrack) !==
        JSON.stringify(mapPreferences.routeTrack)
    ) {
      if (mapPreferences.routeTrack) {
        mapService.updateRouteStyle(mapPreferences.routeTrack);
      }
    }

    // Handle zoom level changes
    if (
      mapPreferences.zoomLevel !== undefined &&
      prevPrefs &&
      prevPrefs.zoomLevel !== mapPreferences.zoomLevel
    ) {
      mapService.setZoom(mapPreferences.zoomLevel);
    }

    // Handle mode changes
    if (
      mapPreferences.mode !== undefined &&
      prevPrefs &&
      prevPrefs.mode !== mapPreferences.mode
    ) {
      mapService.setMode(mapPreferences.mode);
    }

    // Store current prefs for next comparison
    prevPrefsRef.current = { ...mapPreferences };
  }, [mapPreferences, mapService, apiKeys]);

  // Clean up any pending timeouts on unmount
  useEffect(() => {
    return () => {
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
      }
      if (loadDebounceRef.current) {
        clearTimeout(loadDebounceRef.current);
      }
    };
  }, []);
}
